/**
 * @fileoverview Helpers shared by the New Prospect and Edit Prospect forms.
 * @description The pieces both forms need to talk to the backend the same way: the country code
 * every Indian contact number is sent with, making a website an absolute URL, the tier a branch
 * count falls into, and (for Edit) turning a loaded prospect into form values and the validated
 * values back into the PATCH body.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { CreateProspectFormValues } from '@/features/sales/schemas/create-prospect-form.schema'
import type {
  EditProspectRequest,
  ProspectDetails,
  ProspectTierRange,
} from '@/features/sales/types/prospect.types'

/** @description The country code every Indian contact number is sent with. */
export const INDIA_COUNTRY_CODE = '+91'

/**
 * @description The tier a branch count falls into under the active tier scheme.
 * @param {ProspectTierRange[]} ranges - The scheme's ranges.
 * @param {number} branches - The branch count.
 * @returns {string} The tier's name, or an empty string when no range matches.
 */
export function tierNameFor(ranges: ProspectTierRange[], branches: number): string {
  return (
    ranges.find(
      (range) =>
        branches >= range.minBranches &&
        (range.maxBranches === null || branches <= range.maxBranches),
    )?.name ?? ''
  )
}

/**
 * @description Makes a website an absolute URL, as the backend requires: "www.bank.com" becomes
 * "https://www.bank.com".
 * @param {string | undefined} website - The typed website.
 * @returns {string | null} The absolute URL, or null when none was typed.
 */
export function absoluteWebsite(website: string | undefined): string | null {
  const trimmed = website?.trim() ?? ''
  if (!trimmed) return null
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

/**
 * @description The Edit form's starting values: the prospect as it is now. The note field starts
 * empty, because a note typed on Edit is added to the prospect's notes rather than replacing them.
 * @param {ProspectDetails} prospect - The loaded prospect.
 * @returns {CreateProspectFormValues} The form's values.
 */
export function editValuesFromDetails(prospect: ProspectDetails): CreateProspectFormValues {
  return {
    name: prospect.bank.name,
    bankTypeId: prospect.bank.bankTypeId,
    headOffice: prospect.bank.headOfficeLocation,
    regNumber: prospect.bank.registrationNumber,
    website: prospect.bank.website ?? '',
    stateId: prospect.address.stateId,
    city: prospect.address.city,
    district: prospect.address.district,
    pincode: prospect.address.pinCode,
    address: prospect.address.streetAddress,
    branches: prospect.organisation.branchCount,
    ...(prospect.organisation.employeeCount !== null && {
      employees: prospect.organisation.employeeCount,
    }),
    coreBankingSolutionId: prospect.organisation.coreBankingSolutionId,
    existingMis: prospect.organisation.existingMisSolution ?? '',
    contactName: prospect.primaryContact.name,
    contactDesignation: prospect.primaryContact.designation ?? '',
    contactEmail: prospect.primaryContact.email,
    contactPhone: prospect.primaryContact.mobileNumber,
    leadSourceId: prospect.leadSource.id,
    leadRating: prospect.leadRating,
    notes: '',
  }
}

/**
 * @description Turns the validated Edit form into Edit Prospect's body. The registration number
 * and bank type are sent back as loaded (they are the bank's identity), and no owner is sent.
 * @param {CreateProspectFormValues} values - The validated form values.
 * @returns {EditProspectRequest} The PATCH body.
 */
export function toEditProspectRequest(values: CreateProspectFormValues): EditProspectRequest {
  return {
    bank: {
      name: values.name,
      bankTypeId: values.bankTypeId,
      registrationNumber: values.regNumber,
      headOfficeLocation: values.headOffice,
      website: absoluteWebsite(values.website),
    },
    address: {
      streetAddress: values.address,
      stateId: values.stateId,
      city: values.city,
      district: values.district,
      pinCode: values.pincode,
    },
    organisation: {
      branchCount: values.branches,
      employeeCount: values.employees ?? null,
      coreBankingSolutionId: values.coreBankingSolutionId,
      existingMisSolution: values.existingMis || null,
    },
    primaryContact: {
      name: values.contactName,
      designation: values.contactDesignation || null,
      email: values.contactEmail,
      countryCode: INDIA_COUNTRY_CODE,
      mobileNumber: values.contactPhone,
    },
    sales: {
      leadSourceId: values.leadSourceId,
      leadRating: values.leadRating,
      notes: values.notes || null,
    },
  }
}
